import { CENTERCOMPONENTS } from './CenterComponents';
import { ACTIVETAB } from './RightSideComponents';
import { Filters } from '../../Filters/Filters';
import { useMapPanelContext } from './MapPanelContext';
import { CollapsedTable, ReportShipTable } from '../../Reports/Table';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLayoutFlip } from '@ais-ui/custom-hooks';

const Shapes = React.lazy(() =>
  import('../../Shapes').then((m) => ({ default: m.Shapes })),
);
const Reports = React.lazy(() =>
  import('../../Reports/Reports').then((m) => ({ default: m.Reports })),
);
const Events = React.lazy(() =>
  import('../../Events/Events').then((m) => ({ default: m.Events })),
);
const PortAdmin = React.lazy(() =>
  import('../../PortAdmin').then((m) => ({ default: m.PortAdmin })),
);
import {
  useDrawingContext,
  useResetDrawingContext,
} from '@ais-ui/react-openlayers';
import {
  Badge,
  Button,
  ToastView,
  useToastContext,
  useSonnerProps,
} from '@ais-ui/ui-core';
import { Toaster as SonnerToaster } from 'sonner';
import { DrawingMenu } from '../DrawingMenu';
import { PortAdminShapeSelector } from '../../PortAdmin/PortAdminShapeSelector';
import { MultiShapeDrawingProvider } from '@ais-ui/react-openlayers';
import { useReportContext } from '../../Reports/ReportContext';
import { useSession, useOlSessionStatus } from '@ais-ui/websockets';
import { LayerControl } from '../LayerControl/LayerControl';
import { MeasureTool } from '../MeasureTool/MeasureTool';
import { ShipPortSearch } from '../Search/ShipPortSearch';
import { cn } from '@ais-ui/utils';
const Replays = React.lazy(() =>
  import('../../Replay/Replays').then((m) => ({ default: m.Replays })),
);
const Messages = React.lazy(() =>
  import('../../Messages/Messages').then((m) => ({ default: m.Messages })),
);
import { CustomizeReport } from '../../Reports/Customize/CustomizeReport';
import { FilterShips } from '../FilterTargets/FilterShips';
import { ColorTargetsBy } from '../ColorTargetsBy/ColorTargetsBy';
import { ChooseTargetCard } from '../ChooseTargetCard';
import { CollapsedReportDetails } from '../../Reports/CollapsedReportDetails';
import { TargetDetails } from '../../Targets/TargetDetails';
import { GridClasses, useGridClasses } from './useGridClasses';
import { useSettings } from '../../Settings/SettingsProvider';
import { TargetsOnMap } from '../TargetsOnMap/TargetsOnMap';
import { TargetsOnMapTable } from '../TargetsOnMap/TargetsOnMapTable';
import { PortDetails } from '../../Ports/PortDetails';
import { LegendContainer } from '../LegendContainer';
import { RoutesTable } from '../../Routes/RoutesTable';
import { useRoutes } from '../../Routes/RoutesContext';
import { PiBoat } from 'react-icons/pi';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';
import { ResizableTable } from './ResizableTable/ResizableTable';
import { useSelectionState } from '../../../components/SelectionStateProvider';

const MapAtons = React.lazy(() =>
  import('../../Atons/MapAtons').then((m) => ({ default: m.MapAtons })),
);
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { useIsMobile } from '@ais-ui/ui-core';
import { VesselDetailsMobileView } from '../../../components/vessel/VesselDetailsMobileView';
import { FilterDrawer } from '../../../components/filters/FilterDrawer';
import { PiFunnel } from 'react-icons/pi';
import { useFilterContext } from '../FilterContext';
import { UserDropdown } from '../../../components/sidebar/UserDropdown/UserDropdown';

import {
  AttributionBadge,
  CreatedAtBadge,
  CursorPositionBadge,
  MapPanelScale,
  MapPanelZoomButtons,
  RemoteControlBadge,
  ReplayTimeBadge,
  TargetCountBadge,
  TimeZoneBadge,
} from './MetadataBadges';
import { useZoomButtonsSuggestion } from './useZoomButtonsSuggestion';
import { FollowShipIndicator } from './FollowShipIndicator';
import { useResponsivePanels } from './useResponsivePanels';

interface MapPanelTableProps {
  gridClasses: GridClasses;
}

const MapPanelTable = ({ gridClasses }: MapPanelTableProps) => {
  const {
    centerComponent,
    centerComponentData,
    tableExpanded,
    loadingCenterComponentData,
  } = useMapPanelContext();
  const { reportType, reportDetails } = useReportContext();

  if (!tableExpanded) {
    return null;
  }

  return (
    <ResizableTable
      style={gridClasses.reportTableGrid}
      className="self-end cursor-default"
    >
      {centerComponent === CENTERCOMPONENTS.SHIPTABLE && (
        <ReportShipTable
          className="w-full h-full"
          shipData={centerComponentData}
          jobId={reportDetails?.info?.['Job ID'] as string}
          reportType={reportType}
          isLoading={loadingCenterComponentData}
        />
      )}
      {centerComponent === CENTERCOMPONENTS.TARGETSONMAP && (
        <TargetsOnMapTable className="w-full h-full" />
      )}
      {centerComponent === CENTERCOMPONENTS.ROUTES && (
        <RoutesTable className="w-full h-full" />
      )}
    </ResizableTable>
  );
};

/** Expand-from-bottom entrance for the replay panel. */
const ReplayEntrance = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const anim = el.animate(
      [
        { transform: 'scaleY(0)', opacity: 0 },
        { transform: 'scaleY(1)', opacity: 1 },
      ],
      { duration: 200, easing: 'ease-out', fill: 'backwards' },
    );
    // Remove overflow clip after animation so shadows aren't clipped
    anim.onfinish = () => {
      el.style.overflow = 'visible';
    };
  }, []);
  return (
    <div
      ref={ref}
      style={{ ...style, transformOrigin: 'bottom', overflow: 'clip' }}
    >
      {children}
    </div>
  );
};

export const MapPanel = () => {
  const mapPanelRef = useRef<HTMLDivElement>(null);
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const sonnerProps = useSonnerProps();
  const { settings } = useSettings();
  const [isTargetCardExpanded, setIsTargetCardExpanded] = useState(
    settings['ribbonPlugin.settings'].target_card_expanded_by_default ?? true,
  );
  const isMobile = useIsMobile();

  // Mobile-specific state - must be at top level, not inside conditional
  const {
    currentTargetInfo,
    setTargetInfoDetailsList,
    targetInfoDetailsList,
    showVesselDetailCardMobile,
    setShowVesselDetailCardMobile,
    portSelectionMode,
  } = useSelectionState();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const filterContext = useFilterContext();

  const { toastView, setToastView } = useToastContext();
  const { reportType, reportDetails } = useReportContext();
  const { selectedPorts } = useSelectionState();
  const { tableExpanded, activeTab } = useMapPanelContext();
  // Deferred grid update — the grid CSS changes are "discrete" (non-animatable)
  // so when the target card collapses, we delay the grid recomputation by a
  // frame to let the FLIP animation on the card start first. This prevents the
  // jarring stutter where every grid element snaps instantly on collapse.
  //
  // Expanding: grid updates immediately (card grows into the new space).
  // Collapsing: card starts shrinking → grid follows one frame later → FLIP
  //             picks up the metadata/layer controller slide.
  const [gridExpandedDeferred, setGridExpandedDeferred] =
    useState(isTargetCardExpanded);
  useEffect(() => {
    if (isTargetCardExpanded) {
      setGridExpandedDeferred(true);
    } else {
      const id = setTimeout(() => setGridExpandedDeferred(false), 50);
      return () => clearTimeout(id);
    }
  }, [isTargetCardExpanded]);

  const gridExpanded = isTargetCardExpanded || gridExpandedDeferred;
  const gridClasses = useGridClasses(gridExpanded);
  const { dbSession, replaySession } = useSession();
  const { isDown: isOlSessionDown } = useOlSessionStatus();
  const permissions = usePermissions();
  const { shape: routesShape } = useRoutes();

  useZoomButtonsSuggestion();

  useEffect(() => {
    setToastView(ToastView.Map);
    return () => {
      setToastView(ToastView.Normal);
    };
  }, [setToastView]);

  // Measure metadata div position so the Sonner toaster (position:fixed)
  // renders just above the metadata badges, right-aligned with the metadata
  // grid column — mirroring where the old PlacedToastViewport sat.
  const metadataRef = useRef<HTMLDivElement>(null);

  // Smoothly animate metadata position when the grid layout changes.
  // Reacts to both target card expansion (horizontal) and table
  // expand/collapse (vertical — table pushes metadata up a row).
  useLayoutFlip(metadataRef, [gridExpanded, tableExpanded, activeTab], {});

  // FLIP the layer controller — only reacts to target card and table changes.
  // Replay activation doesn't move it (it stays bottom-left), so activeTab
  // is excluded to avoid a false-positive jerk from grid area rebuilds.
  const layerControlRef = useRef<HTMLDivElement>(null);
  useLayoutFlip(layerControlRef, [gridExpanded, tableExpanded], {});

  /** Gap between toast and metadata div top edge (px) */
  const TOAST_METADATA_GAP = 8;
  const [toastBottom, setToastBottom] = useState(100);
  const [toastRight, setToastRight] = useState(0);

  const measureMetadataPosition = useCallback(() => {
    if (metadataRef.current) {
      const rect = metadataRef.current.getBoundingClientRect();
      const newBottom = Math.round(
        window.innerHeight - rect.top + TOAST_METADATA_GAP,
      );
      const newRight = Math.round(window.innerWidth - rect.right);
      if (newBottom > 0) {
        setToastBottom((prev) => (prev === newBottom ? prev : newBottom));
      }
      if (newRight >= 0) {
        setToastRight((prev) => (prev === newRight ? prev : newRight));
      }
    }
  }, []);

  // Preload lazy-loaded sidebar tab chunks and Settings after the map has
  // mounted, so opening a tab or navigating to Settings feels instant.
  // Uses a 2s timeout to let the map finish its initial render first.
  useEffect(() => {
    const preload = () => {
      import('../../Shapes');
      import('../../Reports/Reports');
      import('../../Events/Events');
      import('../../Replay/Replays');
      import('../../Atons/MapAtons');
      import('../../Messages/Messages');
      import('../../PortAdmin');
      import('../../Settings/SettingsPage');
      import('../../Settings/SettingsPageProvider');
    };

    // Safari doesn't support requestIdleCallback
    if (typeof requestIdleCallback !== 'undefined') {
      const id = requestIdleCallback(preload, { timeout: 2000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(preload, 2000);
    return () => clearTimeout(id);
  }, []);

  const resetDrawingContext = useResetDrawingContext();
  const { showDrawPanel } = useDrawingContext();
  const { viewMode } = useViewMode();

  // In Drawing mode, hide most UI to let user focus on the map
  const isDrawingMode = viewMode === ViewMode.Drawing;
  const isKioskMode = viewMode === ViewMode.Kiosk;
  const isMinimalUI = isDrawingMode || isKioskMode;

  const {
    tabChange,
    setTabChange,
    reportDetailsExpanded,
    centerComponent,
    setCenterComponent,
  } = useMapPanelContext();

  // Sidebar panel is active when a tab (other than Replay) is selected
  // and the report details aren't collapsed. This drives the flex wrapper
  // width — the grid no longer handles sidebar allocation.
  const isSidebarPanelActive =
    activeTab !== ACTIVETAB.NONE &&
    activeTab !== ACTIVETAB.REPLAY &&
    reportDetailsExpanded;

  // Responsive panel management — auto-closes the lower-priority panel
  // when both can't fit. Fully self-contained: observes the container,
  // tracks interactions, closes panels, and shows a toast. See hook for details.
  const { setIsTargetExpanded: handleSetTargetCardExpanded } =
    useResponsivePanels({
      containerRef: outerContainerRef,
      isTargetExpanded: isTargetCardExpanded,
      setIsTargetExpanded: setIsTargetCardExpanded,
    });

  // Automatically track the metadata div's position using DOM observers.
  // This reacts to ANY layout change (panel mount/unmount, grid reflow,
  // lazy content loading, window resize) without tracking specific state.
  useEffect(() => {
    const el = metadataRef.current;
    if (!el) return;

    // The grid container is the metadata div's direct parent.
    // Observing it catches panel mounts/unmounts and lazy content loading.
    const gridEl = el.parentElement;

    // Coalesce multiple mutations into a single rAF measurement
    let rafPending = false;
    const scheduleMeasure = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        measureMetadataPosition();
      });
    };

    // Initial measurement
    measureMetadataPosition();

    // Metadata div content size changes (badges added/removed)
    const ro = new ResizeObserver(scheduleMeasure);
    ro.observe(el);

    // Grid container size changes (sidebar flex transition)
    if (gridEl) {
      ro.observe(gridEl);
    }

    // Metadata div grid position changes (React updating the style prop)
    const moStyle = new MutationObserver(scheduleMeasure);
    moStyle.observe(el, { attributes: true, attributeFilter: ['style'] });

    // Panel mounts/unmounts & lazy content loading (e.g. Replay via Suspense)
    let moGrid: MutationObserver | undefined;
    if (gridEl) {
      moGrid = new MutationObserver(scheduleMeasure);
      moGrid.observe(gridEl, { childList: true, subtree: true });
    }

    window.addEventListener('resize', scheduleMeasure);

    return () => {
      ro.disconnect();
      moStyle.disconnect();
      moGrid?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [measureMetadataPosition]);

  // We cannot reset the drawing context from the sidebar, so we have to emit the change through tabChange
  useEffect(() => {
    if (tabChange) {
      resetDrawingContext();
      setTabChange(false);
    }
  }, [resetDrawingContext, tabChange, setTabChange]);

  // Handle Routes Table cleanup when no ports or shapes are selected
  // NOTE: Routes table is now controlled by PortDetails.tsx based on portSelectionMode
  // Also keep routes table open when a shape is selected via ShowRoutesTableDropdownButton

  useEffect(() => {
    // Only clear routes table when no ports are selected AND no shape is selected
    if (
      selectedPorts.length === 0 &&
      !routesShape &&
      centerComponent === CENTERCOMPONENTS.ROUTES
    ) {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    }
  }, [selectedPorts, routesShape, centerComponent, setCenterComponent]);

  const rightSideMapPanelClass = 'cursor-default pointer-events-auto h-full';

  // Mobile view: only show search bar centered at top
  if (isMobile) {
    // Show vessel details full-screen when vessel is selected AND detail card is shown
    if (currentTargetInfo && showVesselDetailCardMobile) {
      return (
        <VesselDetailsMobileView
          onClose={() => {
            setTargetInfoDetailsList([]);
            setShowVesselDetailCardMobile(false);
          }}
        />
      );
    }

    // Calculate active filter count for badge
    const activeFilterCount =
      filterContext.simpleFilters.length + filterContext.customFilters.length;

    return (
      <>
        {/* Top: Search bar only */}
        <div className="absolute top-4 inset-x-4 flex items-center pointer-events-none cursor-default">
          <ShipPortSearch className="pointer-events-auto h-10 w-full" />
        </div>

        {/* Bottom-left: Vertical action bar with 3 buttons */}
        <div className="absolute left-4 bottom-6 flex flex-col gap-3 pointer-events-none cursor-default">
          <Button
            id="mobile-target-details-button"
            variant="outline"
            size="icon"
            onClick={() => setShowVesselDetailCardMobile(true)}
            disabled={!currentTargetInfo}
            className="pointer-events-auto size-12 shrink-0 bg-background dark:bg-background shadow-md border"
          >
            <PiBoat className="size-6" />
          </Button>
          <Button
            id="mobile-filter-button"
            variant="outline"
            size="icon"
            onClick={() => setIsFilterDrawerOpen(true)}
            className="pointer-events-auto size-12 shrink-0 bg-background dark:bg-background shadow-md border relative"
          >
            <PiFunnel className="size-6" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-red-100 bg-primary rounded-full min-w-[1.25rem]">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <div className="pointer-events-auto">
            <UserDropdown expanded={false} mobileMode={true} />
          </div>
        </div>

        <div className="absolute bottom-6 left-20 right-4 flex justify-start pointer-events-none"></div>
        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
        />
        {toastView === ToastView.Map && (
          <SonnerToaster {...sonnerProps} className="pointer-events-auto" />
        )}
      </>
    );
  }

  return (
    <MultiShapeDrawingProvider>
      <div
        ref={outerContainerRef}
        className="absolute top-4 right-4 flex w-[calc(100%-2rem)] h-[calc(100%-2rem)] pointer-events-none gap-2"
      >
        {/* Main grid — handles everything except sidebar panels */}
        <div
          ref={mapPanelRef}
          className="flex-1 min-w-0 bg-transparent pointer-events-none gap-2 cursor-default"
          style={gridClasses.mapPanelGrid}
        >
          {!isMinimalUI && (
            <ShipPortSearch className="pointer-events-auto [grid-area:search] h-10" />
          )}
          <FollowShipIndicator style={gridClasses.followShipGrid} />
          {!isMinimalUI && (
            <ChooseTargetCard style={gridClasses.followShipGrid} />
          )}
          {!isMinimalUI && (
            <div
              ref={layerControlRef}
              style={gridClasses.layerControllerGrid}
              className="flex flex-col space-y-2 h-full justify-end bg-transparent w-fit pointer-events-none"
            >
              <TargetsOnMap className="pointer-events-auto dark:bg-popover shadow-sm"></TargetsOnMap>

              <ColorTargetsBy className="pointer-events-auto dark:bg-popover shadow-sm"></ColorTargetsBy>
              <FilterShips className="pointer-events-auto dark:bg-popover shadow-sm"></FilterShips>
              <MeasureTool className="pointer-events-auto dark:bg-popover shadow-sm"></MeasureTool>
              <LayerControl className="pointer-events-auto dark:bg-popover shadow-sm"></LayerControl>
            </div>
          )}
          <div
            ref={metadataRef}
            style={gridClasses.metadataGrid}
            className="flex flex-col items-end bg-transparent self-end justify-self-end pointer-events-none w-fit space-y-2 [&>*]:pointer-events-auto"
          >
            {!isMinimalUI && !tableExpanded && <CollapsedTable />}
            {!isMinimalUI && !reportDetailsExpanded && (
              <CollapsedReportDetails />
            )}
            {!isMinimalUI && (
              <CustomizeReport
                reportType={reportType}
                report={reportDetails}
                className=""
              ></CustomizeReport>
            )}
            {!isMinimalUI && isOlSessionDown && (
              <Badge variant="destructive" className="shadow-sm text-sm w-fit">
                OLServer down
              </Badge>
            )}
            {!isMinimalUI && !dbSession && (
              <Badge variant="destructive" className="shadow-sm text-sm w-fit">
                Reports down
              </Badge>
            )}
            {!isMinimalUI && !replaySession && (
              <Badge variant="destructive" className="shadow-sm text-sm w-fit">
                Replay down
              </Badge>
            )}

            {!isKioskMode && <MapPanelZoomButtons></MapPanelZoomButtons>}
            {!isKioskMode && <TimeZoneBadge />}
            {!isKioskMode && <MapPanelScale></MapPanelScale>}

            {!isMinimalUI && <CreatedAtBadge></CreatedAtBadge>}

            {!isMinimalUI && <ReplayTimeBadge></ReplayTimeBadge>}
            {!isMinimalUI && <RemoteControlBadge></RemoteControlBadge>}
            {!isKioskMode && <CursorPositionBadge></CursorPositionBadge>}
            {!isMinimalUI && <TargetCountBadge></TargetCountBadge>}
            <AttributionBadge />
          </div>

          {/* Sonner toaster — position:fixed, offset measured from metadata div
              so toasts align with the metadata grid column */}
          {toastView === ToastView.Map && (
            <SonnerToaster
              {...sonnerProps}
              className="pointer-events-auto"
              offset={{ right: toastRight, bottom: toastBottom }}
            />
          )}

          {!isMinimalUI && (
            <LegendContainer
              style={gridClasses.reportLegendGrid}
              className="justify-self-end cursor-default pointer-events-auto"
            />
          )}

          {/* Drawing menu - centered at bottom when active */}
          {showDrawPanel && viewMode === ViewMode.Drawing && (
            <div className="col-start-1 col-end-7 row-start-3 row-end-4 flex items-end justify-center pb-4 pointer-events-none">
              <DrawingMenu className="pointer-events-auto scale-125 origin-bottom">
                <PortAdminShapeSelector />
              </DrawingMenu>
            </div>
          )}

          {!isMinimalUI && centerComponent !== CENTERCOMPONENTS.NONE && (
            <MapPanelTable gridClasses={gridClasses}></MapPanelTable>
          )}
          {!isMinimalUI && (
            <TargetDetails
              isExpanded={isTargetCardExpanded}
              setIsExpanded={handleSetTargetCardExpanded}
              style={gridClasses.targetInfoGrid}
              className="cursor-default pointer-events-auto"
            />
          )}
          {/* PortDetails handles drawing port and opening routes table for 'routes' mode */}
          {/* It only renders the card UI for 'nextport' and 'popout' modes */}
          {!isMinimalUI && selectedPorts.length > 0 && (
            <PortDetails
              style={gridClasses.portInfoGrid}
              className="cursor-default pointer-events-auto"
            ></PortDetails>
          )}

          {/* Replay stays in the grid — it uses unique grid positioning */}
          {activeTab === ACTIVETAB.REPLAY && (
            <Suspense fallback={null}>
              <ReplayEntrance style={gridClasses.replayGrid}>
                <Replays
                  className={cn(
                    'cursor-default pointer-events-auto self-end justify-self-end',
                    isMinimalUI && 'hidden',
                  )}
                />
              </ReplayEntrance>
            </Suspense>
          )}
        </div>

        {/* Sidebar panel wrapper — always mounted, transitions width.
            Container opens first (300ms), content fades in second (200ms).
            This gives immediate visual feedback even for lazy-loaded panels. */}
        <div
          className={cn(
            'shrink-0 h-full overflow-hidden rounded-xl transition-[width] duration-300 ease-out',
            isSidebarPanelActive && !isMinimalUI ? 'w-96 shadow-md' : 'w-0',
          )}
        >
          {/*
            key={activeTab} forces remount on tab switch so the
            enter animation replays for each panel.

            NOTE: In Drawing mode (isMinimalUI), panels stay mounted
            inside the collapsed wrapper to preserve dialog state.
          */}
          <div
            key={activeTab}
            className={cn(
              'w-96 h-full pointer-events-auto cursor-default',
              isSidebarPanelActive &&
                !isMinimalUI &&
                'animate-in fade-in slide-in-from-right-3 duration-200',
            )}
          >
            {activeTab === ACTIVETAB.ATONS && (
              <Suspense fallback={null}>
                <MapAtons className={rightSideMapPanelClass} />
              </Suspense>
            )}
            {activeTab === ACTIVETAB.EVENTS && (
              <Suspense fallback={null}>
                <Events className={rightSideMapPanelClass} />
              </Suspense>
            )}
            {activeTab === ACTIVETAB.FILTERS && (
              <Filters className={rightSideMapPanelClass} />
            )}
            {activeTab === ACTIVETAB.SHAPES && (
              <Suspense fallback={null}>
                <Shapes className={rightSideMapPanelClass} />
              </Suspense>
            )}
            {activeTab === ACTIVETAB.REPORTS && (
              <Suspense fallback={null}>
                <Reports className={rightSideMapPanelClass} />
              </Suspense>
            )}
            {activeTab === ACTIVETAB.MESSAGES && (
              <Suspense fallback={null}>
                <Messages className={rightSideMapPanelClass} />
              </Suspense>
            )}
            {activeTab === ACTIVETAB.PORTADMIN && (
              <Suspense fallback={null}>
                <PortAdmin className={rightSideMapPanelClass} />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </MultiShapeDrawingProvider>
  );
};
